import { Box, Stack, Text, Tooltip } from '@chakra-ui/react';
import { NavigateText } from '../../../components/navigate-text/NavigateText';
import { ProviderName } from '../../../components/provider-name/ProviderName';
import { Column } from '../../../components/resource-grid/ResourceGrid';
import { InsurancePlan } from '../../../types/plans/types';
import {
  rangeToLabel,
  reconstructAgeRange,
} from '../../../utils/ageRanges';
import { getStatusConfig } from '../../../utils/getStatusConfig';

const createIdentityColumns = (
  duplicateProviders: Record<string, string> = {}
): Column<InsurancePlan>[] => [
  // 0. Actions (handled by ResourceGrid standardized action menu)
  {
    header: '',
    accessor: 'actions',
    width: '2.75rem',
    minWidth: '2.75rem',
    isStatic: true,
  },
  // 1. name
  {
    header: 'Provider',
    accessor: 'provider',
    isCustomRender: true,
    width: 'auto',
    minWidth: '7rem',
    isStatic: true,
    render: (row: InsurancePlan) => (
      <Tooltip
        label={
          <Stack spacing="0.25rem">
            <Text fontWeight="600" fontSize="0.875rem">
              Provider Details
            </Text>
            <Text fontSize="0.75rem">
              Full Name:{' '}
              {`${row.provider.firstName || ''} ${
                row.provider.middleName || ''
              } ${row.provider.lastName || ''} ${
                row.provider.suffix || ''
              }`.trim()}
            </Text>
            <Text fontSize="0.75rem">
              Practice: {row.practice.name || '-'}
            </Text>
            <Text fontSize="0.75rem">
              State: {row.practice.state || '-'}
            </Text>
            <Text fontSize="0.75rem">
              Market: {row.marketName || '-'}
            </Text>
            <Text fontSize="0.75rem">
              Tablespace: {row.tablespace || '-'}
            </Text>
            <Text fontSize="0.75rem">Tax ID: {row.taxid || '-'}</Text>
          </Stack>
        }
        hasArrow
        openDelay={400}
      >
        <Box
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          <ProviderName
            provider={{
              id: row.provider.id || '',
              firstname: row.provider.firstName || '',
              lastname: row.provider.lastName || '',
              middlename: row.provider.middleName || null,
              suffix: row.provider.suffix || null,
              customFields: {},
            }}
            duplicateProviders={duplicateProviders}
            mode="table"
            size="sm"
            truncate={true}
          />
        </Box>
      </Tooltip>
    ),
  },
  // 2. practice
  {
    header: 'Practice',
    accessor: 'practice',
    isCustomRender: true,
    width: 'auto',
    minWidth: '8rem',
    isStatic: true,
    render: (row: InsurancePlan) => (
      <Tooltip
        label={
          <Stack spacing="0.25rem">
            <Text fontWeight="600" fontSize="0.875rem">
              Practice Details
            </Text>
            <Text fontSize="0.75rem">
              Full Name: {row.practice.name || '-'}
            </Text>
            <Text fontSize="0.75rem">
              Provider:{' '}
              {`${row.provider.firstName || ''} ${
                row.provider.lastName || ''
              }`.trim()}
            </Text>
            <Text fontSize="0.75rem">
              State: {row.practice.state || '-'}
            </Text>
            <Text fontSize="0.75rem">
              Market: {row.marketName || '-'}
            </Text>
            <Text fontSize="0.75rem">
              Tablespace: {row.tablespace || '-'}
            </Text>
            <Text fontSize="0.75rem">Tax ID: {row.taxid || '-'}</Text>
          </Stack>
        }
        hasArrow
        openDelay={400}
      >
        <Box
          fontSize="0.75rem"
          lineHeight="1rem"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          <NavigateText
            name={`${row.practice.name || ''}`}
            id={row.practice.id || ''}
            resource="practice"
          />
        </Box>
      </Tooltip>
    ),
  },
  // 3. state
  {
    header: 'State',
    accessor: 'practice.state',
    isCustomRender: true,
    width: 'auto',
    minWidth: '4rem',
    isStatic: true,
    render: (row: InsurancePlan) => (
      <Text
        fontSize="0.75rem"
        lineHeight="1rem"
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
      >
        {row.practice.state || '-'}
      </Text>
    ),
  },
  // 4. tablespace
  {
    header: 'Tablespace',
    accessor: 'tablespace',
    isCustomRender: true,
    width: 'auto',
    minWidth: '6rem',
    isStatic: true,
    render: (row: InsurancePlan) => (
      <Text
        fontSize="0.75rem"
        lineHeight="1rem"
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
      >
        {row.tablespace || '-'}
      </Text>
    ),
  },
  // 5. tax id
  {
    header: 'Tax ID',
    accessor: 'taxid',
    isCustomRender: true,
    width: 'auto',
    minWidth: '6rem',
    isStatic: true,
    render: (row: InsurancePlan) => (
      <Text
        fontSize="0.75rem"
        lineHeight="1rem"
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
      >
        {row.taxid || '-'}
      </Text>
    ),
  },
  // 6. market
  {
    header: 'Market',
    accessor: 'marketName',
    isCustomRender: true,
    width: 'auto',
    minWidth: '8rem',
    isStatic: true,
    render: (row: InsurancePlan) => (
      <Tooltip
        label={`Market: ${row.marketName || ''} - Practice: ${
          row.practice.name || ''
        }`}
        hasArrow
        openDelay={400}
        placement="top"
      >
        <Text
          fontSize="0.75rem"
          lineHeight="1rem"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          cursor="help"
        >
          {row.marketName || '-'}
        </Text>
      </Tooltip>
    ),
  },
  // 7. age range
  {
    header: 'Age Range',
    accessor: 'ageRange',
    isCustomRender: true,
    width: 'auto',
    minWidth: '10rem',
    isStatic: true,
    render: (row: InsurancePlan) => {
      // Get the first plan's age range for display
      const firstPlan = row.plans?.[0];
      if (!firstPlan) {
        return (
          <Text fontSize="0.75rem" lineHeight="1rem" color="gray.500">
            No plans
          </Text>
        );
      }

      // Reconstruct age range using the same logic as other components
      const ageRange = reconstructAgeRange({
        minimumAge: firstPlan.minimumAge,
        maximumAge: firstPlan.maximumAge,
        age: firstPlan.age,
        metadata: firstPlan.metadata as
          | {
              minimumAge?: number | null;
              maximumAge?: number | null;
            }
          | undefined,
      });

      const ageLabel = rangeToLabel(ageRange);

      return (
        <Tooltip
          label={
            <Stack spacing="0.25rem">
              <Text fontWeight="600" fontSize="0.875rem">
                Age Range Details
              </Text>
              <Text fontSize="0.75rem">
                Plan:{' '}
                {firstPlan.insurance?.friendlyName || 'Unknown Plan'}
              </Text>
              <Text fontSize="0.75rem">Age Range: {ageLabel}</Text>
              {firstPlan.minimumAge !== undefined && (
                <Text fontSize="0.75rem">
                  Min Age: {firstPlan.minimumAge}
                </Text>
              )}
              {firstPlan.maximumAge !== undefined && (
                <Text fontSize="0.75rem">
                  Max Age: {firstPlan.maximumAge}
                </Text>
              )}
            </Stack>
          }
          hasArrow
          openDelay={400}
          placement="top"
        >
          <Text
            fontSize="0.75rem"
            lineHeight="1rem"
            whiteSpace="normal"
            wordBreak="break-word"
            cursor="help"
          >
            {ageLabel}
          </Text>
        </Tooltip>
      );
    },
  },
];

// Function to generate dynamic columns for plans
const generateDynamicColumns = (
  insurancePlans: InsurancePlan[],
  enhancedBadges: boolean = true,
  planFilter: string[] = []
): Column<InsurancePlan>[] => {
  // Get all unique insurance plans across all providers
  const allPlansMap = new Map<
    string,
    { insurance: { id: string; friendlyName: string | null; companyName: string | null } }
  >();

  insurancePlans.forEach((insurancePlan) => {
    insurancePlan.plans?.forEach((plan) => {
      if (
        plan.insurance?.id &&
        [plan.insurance?.friendlyName, plan.insurance?.companyName].some(
          (name) => name && name.trim()
        )
      ) {
        allPlansMap.set(plan.insurance.id, plan);
      }
    });
  });

  const allPlans = Array.from(allPlansMap.values());

  // Filter plans if planFilter is provided
  const filteredPlans =
    planFilter.length > 0
      ? allPlans.filter((plan) =>
          planFilter.includes(plan.insurance.id)
        )
      : allPlans;

  return filteredPlans.map((plan) => {
    // The whole name, cut nowhere.
    //
    // This used to be sliced to 25 or 30 characters with substring() and an
    // ellipsis appended, so the rest of the name was not in the DOM at all and no
    // amount of CSS could bring it back. Reading a plan meant hovering for the
    // tooltip or opening it. The header now wraps inside its column instead
    // (wrapHeader below), which is what the request asked for.
    const planName =
      [plan.insurance.friendlyName, plan.insurance.companyName]
        .find((name) => name && name.trim()) ?? 'Unknown Plan';

    return {
      header: planName,
      // No fullHeader: that field puts a fixed tooltip on a header that was cut
      // in JavaScript. wrapHeader wraps the whole name and only reaches for a
      // tooltip on the few names too long even for that.
      wrapHeader: true,
      accessor: plan.insurance.id, // Unique identifier for the plan
      linkAccessor: plan.insurance.id,
      isCustomRender: true,
      width: enhancedBadges ? '12rem' : '10rem', // Wider for enhanced badges
      minWidth: enhancedBadges ? '12rem' : '10rem',
      maxWidth: enhancedBadges ? '12rem' : '10rem',
      render: (row: InsurancePlan) => {
        const matchingPlan = row.plans.find(
          (p) => p.insurance.id === plan.insurance.id
        );
        const statusConfig = getStatusConfig(
          matchingPlan?.value,
          matchingPlan?.insurance,
          row.taxid || undefined,
          matchingPlan?.credentialstatus,
          matchingPlan?.metadata
        );
        return (
          <Tooltip
            label={statusConfig.helperText}
            hasArrow
            placement="top-start"
            maxW="25rem"
            minW="15rem"
            offset={[0, 10]}
          >
            <Box
              display="flex"
              height={enhancedBadges ? '3.5rem' : '2.75rem'}
              padding="1rem"
              justifyContent="center"
              alignItems="center"
              gap="0.25rem"
              width="100%"
              maxWidth={enhancedBadges ? '12rem' : '10rem'}
              borderRadius="0.3125rem"
              background={statusConfig.bgColor}
              cursor="help"
              transition="all 0.2s ease-in-out"
              flexDirection={enhancedBadges ? 'column' : 'row'}
              flexShrink={0}
              overflow="hidden"
              position="relative"
              data-testid="chip"
            >
              <Text
                fontSize="0.75rem"
                fontWeight="600"
                lineHeight="1.1"
                color="white"
                textAlign="left"
                whiteSpace="normal"
                wordBreak="break-word"
                width="100%"
                maxW="100%"
                flex="0 0 auto"
              >
                {statusConfig.label}
              </Text>
              {enhancedBadges && (
                <Text
                  fontSize="0.625rem"
                  fontWeight="400"
                  lineHeight="1.0"
                  color="white"
                  textAlign="left"
                  opacity="0.9"
                  whiteSpace="normal"
                  wordBreak="break-word"
                  width="100%"
                  maxW="100%"
                  flex="0 0 auto"
                >
                  {statusConfig.helperText.split(' | ')[0]}
                </Text>
              )}
            </Box>
          </Tooltip>
        );
      },
    };
  });
};
export const plansColumns = (
  insurancePlans: InsurancePlan[],
  enhancedBadges: boolean = true,
  planFilter: string[] = []
): Column<InsurancePlan>[] => {
  const dynamicColumns = generateDynamicColumns(
    insurancePlans,
    enhancedBadges,
    planFilter
  );

  return [...createIdentityColumns(), ...dynamicColumns];
};

export { createIdentityColumns };
